import { getModuleNameFromCallStack } from './utils';
import { WebSocketConnection } from './connection/WebSocketConnection';
import { TSchema, Static } from '@sinclair/typebox';
import { validateBySchema } from '../common/validation';
import { WebSocketMessage, RequestOptions, Channel, ReplyFunction } from '../common/types';
import logger from '@ayocore/platform/common/helpers/logger';
import { AuthController } from '@ayocore/platform/identity/client/AuthController';

// Create WebSocket instance - singleton management handled in constructor
const websocket = new WebSocketConnection();

// Track if we've set up auth-aware connection (to avoid multiple subscriptions)
let authConnectionSetup = false;

/**
 * Check if we should connect now based on auth state.
 * Returns:
 * - true: connect immediately (auth done and authenticated, or auth not available)
 * - false: defer connection (auth is loading, or auth done but not authenticated)
 */
function shouldConnectNow(): boolean {
  try {
    const authController = AuthController.getInstance();
    const authState = authController.getSnapshot();

    // If auth is still loading, defer connection
    if (authState.isLoading) {
      logger.debug('[getChannel] Auth is loading, deferring WebSocket connection');
      return false;
    }

    // If auth is done and user is authenticated, connect
    if (authState.isAuthenticated) {
      return true;
    }

    // If auth is done but user is not authenticated, don't connect
    logger.debug('[getChannel] User not authenticated, skipping WebSocket connection');
    return false;
  } catch {
    // AuthController not available or error accessing it - proceed with connection (backward compatible)
    logger.debug('[getChannel] AuthController not available, proceeding with connection');
    return true;
  }
}

/**
 * Set up one-time subscription to connect when auth completes.
 * Only called once per application lifecycle.
 */
function setupAuthAwareConnection(): void {
  if (authConnectionSetup) {
    return; // Already set up
  }

  try {
    const authController = AuthController.getInstance();

    // One-time subscription: when auth loading completes, try to connect if authenticated
    const unsubscribe = authController.subscribe(() => {
      const authState = authController.getSnapshot();

      // When loading completes
      if (!authState.isLoading) {
        // Unsubscribe after first completion
        unsubscribe();
        authConnectionSetup = false; // Allow re-setup if needed

        // Connect if authenticated
        if (authState.isAuthenticated) {
          logger.debug('[getChannel] Auth completed, user authenticated - connecting WebSocket');
          websocket.connect();
        } else {
          logger.debug(
            '[getChannel] Auth completed, user not authenticated - skipping WebSocket connection'
          );
        }
      }
    });

    authConnectionSetup = true;
  } catch {
    // AuthController not available - that's ok, we'll connect anyway
    logger.debug('[getChannel] Could not set up auth-aware connection, will connect normally');
  }
}

/**
 * Получить типизированный канал для модуля.
 *
 * По умолчанию типы не навязаны (TReq — unknown, TRes — unknown),
 * их можно задать через withSchema(schemaReq, schemaRes).
 */
export function getChannel<TReq = unknown, TRes = unknown>(
  channelName?: string
): Channel<TReq, TRes> {
  const moduleName = channelName ? channelName : getModuleNameFromCallStack();

  // Auto-connect on first use, but check auth state first
  if (shouldConnectNow()) {
    websocket.connect();
  } else {
    // Auth is loading - set up subscription to connect when auth completes
    setupAuthAwareConnection();
  }

  // Валидаторы запроса и ответа — по умолчанию pass-through (универсальные)
  let validateRequestInternal: (data: unknown) => unknown = data => data;
  let validateResponseInternal: (data: unknown) => unknown = data => data;

  const channel: Channel<TReq, TRes> = {
    send(data: TReq) {
      try {
        const validatedData = validateRequestInternal(data) as TReq;
        logger.info(`📤 [${moduleName}] Message sent:`, validatedData);

        const webSocketMessage: WebSocketMessage = {
          type: 'broadcast',
          channelId: moduleName,
          payload: validatedData,
          messageId: websocket.generateMessageId(),
        };

        websocket.send(webSocketMessage);
      } catch (error) {
        logger.error(`❌ [${moduleName}] Validation failed:`, error);
        throw error;
      }
    },

    async request(data: TReq, options?: RequestOptions): Promise<TRes> {
      try {
        const validatedData = validateRequestInternal(data) as TReq;
        logger.info(`📤 [${moduleName}] Request sent:`, validatedData);

        const response = await websocket.request(moduleName, validatedData, options);
        return validateResponseInternal(response) as TRes;
      } catch (error) {
        logger.error(`❌ [${moduleName}] Request failed:`, error);
        throw error;
      }
    },

    onMessage(handler: (message: TRes) => void) {
      return websocket.onMessage(webSocketMessage => {
        if (
          webSocketMessage.channelId === moduleName &&
          webSocketMessage.type === 'channel_message'
        ) {
          try {
            const validatedMessage = validateResponseInternal(webSocketMessage.payload) as TRes;
            // logger.info(`📨 [${moduleName}] Message received:`, validatedMessage);
            handler(validatedMessage);
          } catch (error) {
            // Silent ignore invalid incoming messages
            logger.warn(
              `⚠️ [${moduleName}] Ignoring invalid message: ${error}`,
              webSocketMessage.payload
            );
          }
        }
      });
    },

    onRequest(handler: (message: TReq, reply: ReplyFunction<TRes>) => void) {
      return websocket.onMessage(webSocketMessage => {
        if (webSocketMessage.channelId === moduleName && webSocketMessage.type === 'request') {
          try {
            const validatedMessage = validateRequestInternal(webSocketMessage.payload) as TReq;
            logger.info(`📨 [${moduleName}] Request received:`, validatedMessage);

            // Create reply function
            const reply: ReplyFunction<TRes> = (data: TRes) => {
              // Необязательно валидировать ответ, но если задана схема ответа — провалидируем
              const validatedResponse = validateResponseInternal(data) as TRes;
              const responseMessage: WebSocketMessage = {
                type: 'response',
                channelId: moduleName,
                payload: validatedResponse,
                messageId: websocket.generateMessageId(),
                replyTo: webSocketMessage.messageId,
              };
              websocket.send(responseMessage);
            };

            reply.error = (message: string) => {
              const errorResponse: WebSocketMessage = {
                type: 'response',
                channelId: moduleName,
                payload: null,
                messageId: websocket.generateMessageId(),
                replyTo: webSocketMessage.messageId,
                error: message,
              };
              websocket.send(errorResponse);
            };

            handler(validatedMessage, reply);
          } catch (error) {
            logger.error(`❌ [${moduleName}] Request validation failed:`, error);
            // Send error response
            const errorResponse: WebSocketMessage = {
              type: 'response',
              channelId: moduleName,
              payload: null,
              messageId: websocket.generateMessageId(),
              replyTo: webSocketMessage.messageId,
              error: `Validation failed: ${error}`,
            };
            websocket.send(errorResponse);
          }
        }
      });
    },

    onConnectionLost(handler) {
      return websocket.onDisconnect(handler);
    },

    onConnectionStart(handler) {
      return websocket.onConnect(handler);
    },

    onError(handler) {
      return websocket.onError(handler);
    },

    isConnected() {
      return websocket.isConnected();
    },

    withSchema<SReq extends TSchema, SRes extends TSchema = SReq>(
      schemaReq: SReq,
      schemaRes?: SRes
    ): Channel<Static<SReq>, Static<SRes>> {
      logger.info(
        `🔒 [${moduleName}] Adding schema validation`,
        (schemaReq.$id || 'req:unnamed') + (schemaRes?.$id ? `, res:${schemaRes.$id}` : '')
      );

      // Обновляем валидаторы (универсальные)
      validateRequestInternal = (data: unknown): unknown => {
        return validateBySchema(schemaReq, data, true) as Static<SReq>;
      };
      if (schemaRes) {
        validateResponseInternal = (data: unknown): unknown => {
          return validateBySchema(schemaRes, data, true) as Static<SRes>;
        };
      } else {
        validateResponseInternal = (data: unknown): unknown => {
          return validateBySchema(schemaReq, data, true) as Static<SReq>;
        };
      }

      return channel as unknown as Channel<Static<SReq>, Static<SRes>>;
    },
  };

  return channel;
}

// Export additional utilities for debugging
export function getConnectionStatus() {
  return websocket.getConnectionStatus();
}

export function disconnectWebSocket() {
  websocket.disconnect();
}

export function reconnectWebSocket() {
  websocket.forceReconnect();
}
